import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Collapse, Card, AutoComplete, Input, Empty } from 'antd';
import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const FAQPage = () => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [activeKeys, setActiveKeys] = useState({});
  const questionRefs = useRef({});

  const faqData = [
    {
      category: t('faq.categories.general'),
      questions: [
        {
          q: t('faq.general.whatIsAnimap.q'),
          a: t('faq.general.whatIsAnimap.a')
        },
        {
          q: t('faq.general.createAccount.q'),
          a: t('faq.general.createAccount.a')
        },
        {
          q: t('faq.general.languages.q'),
          a: t('faq.general.languages.a')
        }
      ]
    },
    {
      category: t('faq.categories.products'),
      questions: [
        {
          q: t('faq.products.howToPurchase.q'),
          a: t('faq.products.howToPurchase.a')
        },
        {
          q: t('faq.products.multipleSellers.q'),
          a: t('faq.products.multipleSellers.a')
        },
        {
          q: t('faq.products.paymentMethods.q'),
          a: t('faq.products.paymentMethods.a')
        },
        {
          q: t('faq.products.trackOrder.q'),
          a: t('faq.products.trackOrder.a')
        }
      ]
    },
    {
      category: t('faq.categories.services'),
      questions: [
        {
          q: t('faq.services.howToBook.q'),
          a: t('faq.services.howToBook.a')
        },
        {
          q: t('faq.services.cancelAppointment.q'),
          a: t('faq.services.cancelAppointment.a')
        },
        {
          q: t('faq.services.typesAvailable.q'),
          a: t('faq.services.typesAvailable.a')
        }
      ]
    },
    {
      category: t('faq.categories.myAnimals'),
      questions: [
        {
          q: t('faq.myAnimals.howToAdd.q'),
          a: t('faq.myAnimals.howToAdd.a')
        },
        {
          q: t('faq.myAnimals.whyAdd.q'),
          a: t('faq.myAnimals.whyAdd.a')
        }
      ]
    },
    {
      category: t('faq.categories.partners'),
      questions: [
        {
          q: t('faq.partners.howToBecome.q'),
          a: t('faq.partners.howToBecome.a')
        },
        {
          q: t('faq.partners.requirements.q'),
          a: t('faq.partners.requirements.a')
        }
      ]
    }
  ];

  // Flatten all questions for search
  const allQuestions = useMemo(() => {
    const questions = [];
    faqData.forEach((section, sectionIndex) => {
      section.questions.forEach((item, qIndex) => {
        questions.push({
          ...item,
          category: section.category,
          sectionIndex,
          questionIndex: qIndex,
          id: `${sectionIndex}-${qIndex}`
        });
      });
    });
    return questions;
  }, [faqData]);

  // Filter questions based on search
  const searchOptions = useMemo(() => {
    if (!searchValue.trim()) return [];

    const searchLower = searchValue.toLowerCase();
    return allQuestions
      .filter(item =>
        item.q.toLowerCase().includes(searchLower) ||
        item.a.toLowerCase().includes(searchLower)
      )
      .map(item => ({
        value: item.id,
        label: (
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontWeight: 500 }}>{item.q}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{item.category}</Text>
          </div>
        ),
        question: item
      }));
  }, [searchValue, allQuestions]);

  // Filter displayed FAQ data based on search
  const filteredFaqData = useMemo(() => {
    if (!searchValue.trim()) return faqData;

    const searchLower = searchValue.toLowerCase();
    return faqData
      .map(section => ({
        ...section,
        questions: section.questions.filter(item =>
          item.q.toLowerCase().includes(searchLower) ||
          item.a.toLowerCase().includes(searchLower)
        )
      }))
      .filter(section => section.questions.length > 0);
  }, [searchValue, faqData]);

  // Handle selection from autocomplete
  const handleSelect = (value, option) => {
    const { question } = option;
    setSearchValue('');

    // Expand the selected question
    setActiveKeys(prev => ({
      ...prev,
      [question.sectionIndex]: String(question.questionIndex)
    }));

    // Scroll to the question after a short delay
    setTimeout(() => {
      const element = questionRefs.current[question.id];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleCollapseChange = (sectionIndex, keys) => {
    setActiveKeys(prev => ({
      ...prev,
      [sectionIndex]: keys
    }));
  };

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <QuestionCircleOutlined style={{ fontSize: 48, color: '#9FB18C', marginBottom: 16 }} />
        <Title level={2}>{t('faq.title')}</Title>
        <Paragraph type="secondary">
          {t('faq.subtitle')}
        </Paragraph>
      </div>

      {/* Search Field */}
      <div style={{ marginBottom: 24 }}>
        <AutoComplete
          style={{ width: '100%' }}
          options={searchOptions}
          onSelect={handleSelect}
          onSearch={setSearchValue}
          value={searchValue}
        >
          <Input
            size="large"
            placeholder={t('faq.searchPlaceholder', 'Search for questions...')}
            prefix={<SearchOutlined style={{ color: '#9FB18C' }} />}
            allowClear
          />
        </AutoComplete>
      </div>

      {filteredFaqData.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '40px 0' }}>
          <Empty
            description={t('faq.noResults', 'No questions found matching your search')}
          />
        </Card>
      ) : (
        filteredFaqData.map((section, index) => {
          const originalSectionIndex = faqData.findIndex(s => s.category === section.category);
          return (
            <Card
              key={originalSectionIndex}
              title={section.category}
              style={{ marginBottom: 16 }}
              headStyle={{ backgroundColor: '#f9f9f9' }}
            >
              <Collapse
                accordion
                bordered={false}
                expandIconPosition="end"
                activeKey={activeKeys[originalSectionIndex]}
                onChange={(keys) => handleCollapseChange(originalSectionIndex, keys)}
              >
                {section.questions.map((item, qIndex) => {
                  const originalQIndex = faqData[originalSectionIndex]?.questions.findIndex(
                    q => q.q === item.q
                  );
                  const questionId = `${originalSectionIndex}-${originalQIndex}`;
                  return (
                    <Panel
                      header={<span style={{ fontWeight: 500 }}>{item.q}</span>}
                      key={String(originalQIndex)}
                    >
                      <div ref={(el) => { questionRefs.current[questionId] = el; }}>
                        <Paragraph style={{ margin: 0 }}>{item.a}</Paragraph>
                      </div>
                    </Panel>
                  );
                })}
              </Collapse>
            </Card>
          );
        })
      )}

      <Card style={{ marginTop: 24, textAlign: 'center', backgroundColor: '#f9f9f9' }}>
        <Title level={4}>{t('faq.stillHaveQuestions')}</Title>
        <Paragraph>
          {t('faq.contactUs')}{' '}
          <a href="mailto:support@animap.gr" style={{ color: '#9FB18C', fontWeight: 500 }}>
            support@animap.gr
          </a>
        </Paragraph>
      </Card>
    </div>
  );
};

export default FAQPage;
